import { useState } from 'react';

import { type DragEndEvent } from '@dnd-kit/core';
import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { type ProductsOrderingRequest } from '../aliases';
import { rearrangeProducts } from '../api';

import { useIsViewingOwnShop } from './useIsViewingOwnShop';
import { useShopTracking } from './useShopTracking';

import {
  type RearrangeChangeSet,
  type ShopListProduct,
} from '@/modules/shop/types';

type Args = {
  products: ShopListProduct[];
  queryKey: QueryKey;
};

export function useRearrangeShop({ products, queryKey }: Args) {
  const isViewingOwnShop = useIsViewingOwnShop();
  const { sendRearrangeShopEvent } = useShopTracking();

  const queryClient = useQueryClient();

  const [isRearranging, setIsRearranging] = useState(false);

  // changeset keeps track of both the indices and product IDs of reordered items.
  // Indices are used for UI reordering logic, whilst IDs are used by the backend
  // to produce a reliable sort accounting for sold, banned etc.
  const [changeset, setChangeset] = useState<Array<RearrangeChangeSet>>([]);

  function applyChangeSet<T>(array: T[], changes: RearrangeChangeSet[]): T[] {
    const clone = array.slice();

    for (const { from, to } of changes) {
      const [item] = clone.splice(from, 1);
      clone.splice(to, 0, item);
    }

    return clone;
  }

  function start() {
    setIsRearranging(true);
  }

  function stop() {
    setIsRearranging(false);
    setChangeset([]);
  }

  const mutation = useMutation({
    mutationFn: (data: ProductsOrderingRequest) => rearrangeProducts(data),
    onSuccess: () => {
      sendRearrangeShopEvent();
      stop();
      queryClient.invalidateQueries({ queryKey });
      mutation.reset();
    },
  });

  function save() {
    mutation.mutate({
      move_sold_to_end: false,
      changeset: changeset.map(
        ({ from_product_id, to_product_id, how_many = 1 }) => ({
          from_product_id,
          to_product_id,
          how_many,
        })
      ),
    });
  }

  const rearrangedProducts = products
    ? applyChangeSet(products, changeset)
    : [];

  function swap(event: DragEndEvent) {
    const { active, over } = event;

    const from_product_id = Number(active?.id);
    const to_product_id = Number(over?.id);

    if (
      !from_product_id ||
      !to_product_id ||
      from_product_id === to_product_id
    ) {
      return;
    }

    const from = rearrangedProducts.findIndex(
      (product) => product.id === active?.id
    );
    const to = rearrangedProducts.findIndex(
      (product) => product.id === over?.id
    );

    setChangeset((prev) => [
      ...prev,
      {
        from,
        from_product_id,
        to,
        to_product_id,
        how_many: 1,
      },
    ]);
  }

  const canRearrange = isViewingOwnShop && products && products.length > 1;

  return {
    mutation,
    isRearranging,
    canRearrange,
    start,
    stop,
    save,
    swap,
    changeset,
    rearrangedProducts,
  };
}
